import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
    Send,
    ArrowLeft,
    CheckCircle,
    Clock,
    AlertCircle,
    User,
    Building2,
    ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
    pending: 'bg-yellow-100 text-yellow-800',
    in_review: 'bg-blue-100 text-blue-800',
    resolved: 'bg-green-100 text-green-800',
    closed: 'bg-gray-100 text-gray-800',
};

export default function TicketDetail() {
    const { ticketId } = useParams();
    const navigate = useNavigate();
    const { getAuthHeader, agency, customer, userType, loading: authLoading } = useAuth();
    const currentUser = agency || customer || {}; // Admin doesn't have an ID object in context usually

    const [ticket, setTicket] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);

    const [newMessage, setNewMessage] = useState('');
    const [sending, setSending] = useState(false);
    const messagesEndRef = useRef(null);

    // Initial fetch
    useEffect(() => {
        if (!authLoading && ticketId) {
            fetchTicketDetails(true);
        }
    }, [ticketId, authLoading]);

    // Polling for new messages
    useEffect(() => {
        if (!ticketId) return;

        const interval = setInterval(() => {
            fetchTicketDetails(false); // Silent update
        }, 5000); // Poll every 5 seconds

        return () => clearInterval(interval);
    }, [ticketId]);

    useEffect(() => {
        // Only scroll on initial load or if user is near bottom
        if (loading) return;
        scrollToBottom();
    }, [messages.length, loading]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const fetchTicketDetails = async (showLoading = false) => {
        if (showLoading) setLoading(true);
        try {
            const response = await axios.get(`${API_URL}/api/tickets/${ticketId}`, {
                headers: getAuthHeader()
            });
            setTicket(response.data.ticket);

            // Only update messages if length changed to avoid jitter, 
            // or just update always if we want to catch edits (though we don't have edits yet)
            // A simple comparison or just setting it is fine for now.
            // But to avoid "jumping", let's just set it. React handles diffing.
            setMessages(response.data.messages);
        } catch (error) {
            console.error('Failed to fetch ticket:', error);
            if (showLoading) {
                toast.error('Failed to load ticket details');
                // navigate(-1); // Don't navigate back automatically on error, let user decide or retry
            }
        } finally {
            if (showLoading) setLoading(false);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim()) return;

        setSending(true);
        try {
            const res = await axios.post(`${API_URL}/api/tickets/${ticketId}/messages`, {
                message: newMessage
            }, { headers: getAuthHeader() });

            setMessages([...messages, {
                id: res.data.msg_id,
                ticket_id: ticketId,
                sender_id: currentUser.id || 'admin', // Admin might not have ID
                sender_type: userType,
                sender_name: 'Me',
                message: newMessage,
                created_at: new Date().toISOString()
            }]);
            setNewMessage('');
        } catch (error) {
            toast.error('Failed to send message');
        } finally {
            setSending(false);
        }
    };

    const handleCloseTicket = async () => {
        try {
            // If admin, can close. If user, maybe we need a user endpoint? 
            // For now, assuming only admin closes via status update or user requests it.
            // But let's try calling the status update if we are admin.
            if (userType === 'admin') {
                await axios.put(`${API_URL}/api/admin/tickets/${ticketId}/status?status=closed`, {}, {
                    headers: getAuthHeader()
                });
                setTicket(prev => ({ ...prev, status: 'closed' }));
                toast.success('Ticket closed');
            } else {
                toast.info('Please request an admin to close this ticket.');
            }
        } catch (error) {
            toast.error('Failed to close ticket');
        }
    };

    if (loading) return <div className="p-8 text-center text-navy-500">Loading discussion...</div>;

    if (!ticket) return null;

    return (
        <div className="p-4 lg:p-8 max-w-5xl mx-auto h-[calc(100vh-100px)] flex flex-col">
            {/* Header */}
            <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div>
                        <div className="flex items-center gap-3 mb-1">
                            <h1 className="text-xl font-heading font-bold text-navy-900">
                                {ticket.subject}
                            </h1>
                            <Badge variant="secondary" className={STATUS_COLORS[ticket.status]}>
                                {ticket.status.replace('_', ' ').toUpperCase()}
                            </Badge>
                        </div>
                        <p className="text-sm text-navy-500">
                            Ticket #{ticket.id.slice(0, 8)} • {new Date(ticket.created_at).toLocaleString()}
                        </p>
                    </div>
                </div>

                {ticket.status !== 'closed' && userType === 'admin' && (
                    <Button variant="outline" onClick={handleCloseTicket} className="text-red-600 border-red-200 bg-red-50 hover:bg-red-100">
                        Close Ticket
                    </Button>
                )}
            </div>

            {/* Chat Area */}
            <Card className="flex-1 flex flex-col overflow-hidden bg-navy-50">
                <CardContent className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Initial Problem Description */}
                    <div className="flex gap-4 max-w-3xl">
                        <div className="w-10 h-10 rounded-full bg-navy-200 flex items-center justify-center flex-shrink-0">
                            <User className="w-5 h-5 text-navy-600" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <span className="font-semibold text-navy-900">{ticket.user_name}</span>
                                <span className="text-xs text-navy-400">Owner</span>
                            </div>
                            <div className="bg-white p-4 rounded-2xl rounded-tl-none shadow-sm text-navy-800">
                                <p className="whitespace-pre-wrap">{ticket.description}</p>
                            </div>
                        </div>
                    </div>

                    {/* Messages */}
                    {messages.slice(1).map((msg) => {
                        const isMe = (userType === 'admin' && msg.sender_type === 'admin') ||
                            (userType !== 'admin' && msg.sender_id === currentUser?.id);

                        return (
                            <div
                                key={msg.id}
                                className={`flex gap-4 max-w-3xl ${isMe ? 'ml-auto flex-row-reverse' : ''
                                    }`}
                            >
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isMe ? 'bg-navy-900 text-white' : 'bg-navy-200 text-navy-600'
                                    }`}>
                                    {msg.sender_type === 'admin' ? (
                                        <ShieldCheck className="w-5 h-5" />
                                    ) : (
                                        <User className="w-5 h-5" />
                                    )}
                                </div>
                                <div className={`${isMe ? 'text-right' : ''}`}>
                                    <div className={`flex items-center gap-2 mb-1 ${isMe ? 'justify-end' : ''}`}>
                                        <span className="font-semibold text-navy-900">{isMe ? 'Me' : msg.sender_name}</span>
                                        <span className="text-xs text-navy-400">
                                            {new Date(msg.created_at).toLocaleString([], { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                    </div>
                                    <div className={`p-4 rounded-2xl shadow-sm text-navy-800 ${isMe
                                        ? 'bg-sky-100 rounded-tr-none'
                                        : 'bg-white rounded-tl-none'
                                        }`}>
                                        <p className="whitespace-pre-wrap text-left">{msg.message}</p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    <div ref={messagesEndRef} />
                </CardContent>

                {/* Input Area */}
                <div className="p-4 bg-white border-t border-navy-100">
                    <form onSubmit={handleSendMessage} className="flex gap-4">
                        <Input
                            value={newMessage}
                            onChange={e => setNewMessage(e.target.value)}
                            placeholder={ticket.status === 'closed' ? 'This ticket is closed.' : "Type your message..."}
                            disabled={ticket.status === 'closed' || sending}
                            className="flex-1"
                        />
                        <Button
                            type="submit"
                            disabled={ticket.status === 'closed' || sending || !newMessage.trim()}
                            className="bg-navy-900 hover:bg-navy-800"
                        >
                            {sending ? (
                                <Clock className="w-4 h-4 animate-spin" />
                            ) : (
                                <Send className="w-4 h-4" />
                            )}
                        </Button>
                    </form>
                </div>
            </Card>
        </div>
    );
}
